import { maybe } from '@passionware/monads';
import { z } from 'zod';

const globalSearchEntry$ = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullish().transform(maybe.getOrUndefined).optional(),
  url: z.string(),
  badge: z
    .object({
      text: z.string(),
      color: z.string().nullish().transform(maybe.getOrUndefined).optional()
    })
    .nullish()
    .transform(maybe.getOrUndefined)
    .optional(),
  avatar: z
    .object({
      url: z.string().nullish().transform(maybe.getOrUndefined).optional(),
      fallback: z.string().nullish().transform(maybe.getOrUndefined).optional(),
      ascii: z.string().nullish().transform(maybe.getOrUndefined).optional()
    })
    .nullish()
    .transform(maybe.getOrUndefined)
    .optional(),
  group: z.string().nullish().transform(maybe.getOrUndefined).optional(),
  shortcut: z.string().nullish().transform(maybe.getOrUndefined).optional()
});

const globalSearchEntries$ = z.array(globalSearchEntry$);

/** localStorage: per browsing-scope buckets (`org` | `ws:{id}`). */
export type GlobalSearchRecentsByScope = Record<
  string,
  z.infer<typeof globalSearchEntries$>
>;

export const ORG_GLOBAL_SEARCH_RECENTS_KEY = 'org';

export function globalSearchRecentsScopeKey(
  enterpriseWorkspaceId: number | null | undefined
): string {
  return enterpriseWorkspaceId != null
    ? `ws:${enterpriseWorkspaceId}`
    : ORG_GLOBAL_SEARCH_RECENTS_KEY;
}

export const globalSearchRecentsByScope$ = z
  .unknown()
  .transform((raw): GlobalSearchRecentsByScope => {
    if (raw == null) {
      return {};
    }
    // Legacy flat array → org bucket
    const asLegacy = globalSearchEntries$.safeParse(raw);
    if (asLegacy.success) {
      return asLegacy.data.length > 0
        ? { [ORG_GLOBAL_SEARCH_RECENTS_KEY]: asLegacy.data }
        : {};
    }
    const asMap = z.record(z.string(), globalSearchEntries$).safeParse(raw);
    if (asMap.success) {
      return asMap.data;
    }
    console.warn('Error parsing globalSearchRecentsByScope$', raw);
    return {};
  });

/** @deprecated Use {@link globalSearchRecentsByScope$} — kept name for older imports. */
export const globalSearch$ = globalSearchRecentsByScope$;
